import { writeContract, WriteContractParameters, getChainId, estimateFeesPerGas } from '@wagmi/core'
import { Abi, ContractFunctionArgs, ContractFunctionName, Hex } from 'viem'
import { Config, useReadContract, UseReadContractParameters, useReadContracts, UseReadContractsParameters } from 'wagmi'

type ContractCall<T extends Abi, TFunctionName extends ContractFunctionName<T, 'pure' | 'view'>> = {
	functionName: TFunctionName
	args?: ContractFunctionArgs<T, 'pure' | 'view', TFunctionName>
}

export function makeWriteContractAsync<T extends Abi, TConfig extends Config>(
	config: TConfig,
	{ abi, address }: { abi: T; address: Hex }
) {
	return async function <
		functionName extends ContractFunctionName<T, 'nonpayable' | 'payable'>,
		args extends ContractFunctionArgs<T, 'nonpayable' | 'payable', functionName>,
		chainId extends TConfig['chains'][number]['id'] = TConfig['chains'][number]['id']
	>(
		params: Omit<WriteContractParameters<T, functionName, args, TConfig, chainId>, 'abi' | 'address'> & {
			functionName: functionName
			args?: args
		}
	) {
		const chainId = await getChainId(config)
		const { maxFeePerGas, maxPriorityFeePerGas } = await estimateFeesPerGas(config, { chainId })

		return writeContract<TConfig, T, functionName, args, chainId>(config, {
			__mode: 'prepared',
			abi,
			address,
			maxFeePerGas,
			maxPriorityFeePerGas,
			...params
		} as unknown as WriteContractParameters<T, functionName, args, TConfig, chainId>)
	}
}

export function makeReadContract<T extends Abi>(abi: T, useAddress: () => Hex) {
	return function <
		functionName extends ContractFunctionName<T, 'pure' | 'view'>,
		args extends ContractFunctionArgs<T, 'pure' | 'view', functionName>
	>(
		params: Omit<UseReadContractParameters<T, functionName, args>, 'abi' | 'address'> & {
			functionName: functionName
			args?: args
		}
	) {
		const smartcontractAddress = useAddress()
		return useReadContract<T, functionName, args>({
			abi,
			address: smartcontractAddress,
			...params
		} as unknown as UseReadContractParameters<T, functionName, args>)
	}
}

export function makeReadContracts<T extends Abi>(abi: T, useAddress: () => Hex) {
	return function <
		TFunctionName extends ContractFunctionName<T, 'pure' | 'view'>,
		TArgs extends ContractFunctionArgs<T, 'pure' | 'view', TFunctionName>
	>(calls: ContractCall<T, TFunctionName>[]) {
		const smartcontractAddress = useAddress()

		const contracts = calls.map(call => ({
			abi,
			address: smartcontractAddress,
			functionName: call.functionName,
			args: call.args
		}))

		return useReadContracts({
			contracts: contracts as UseReadContractsParameters['contracts']
		})
	}
}
